import React, { useEffect, useState } from "react";
import { Container, Card, Row, Col, Form, Button, Badge, Spinner, Table, InputGroup, Alert } from "react-bootstrap";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { fires } from "../../firebase";
import firebase from "firebase/app";
import "firebase/firestore";
import uuid from "react-uuid";

export default function BusinessPriceManagement() {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const { id: routeEventId } = useParams();
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [businesses, setBusinesses] = useState([]);
  const [savingId, setSavingId] = useState("");
  const [query, setQuery] = useState("");
  const [uploadingImageId, setUploadingImageId] = useState("");
  const [showPhotoSelector, setShowPhotoSelector] = useState({});

  // Check admin access with management role
  useEffect(() => {
    const checkAdminAccess = async () => {
      if (!currentUser) {
        navigate('/');
        setLoading(false);
        return;
      }

      try {
        const adminDoc = await fires.collection('admin').doc(currentUser.uid).get();
        if (!adminDoc.exists) {
          setIsAdmin(false);
          setLoading(false);
          setError("Access denied. Admin privileges required.");
          navigate('/');
          return;
        }

        const adminData = adminDoc.data();
        if (!adminData.roles || !adminData.roles.includes('management')) {
          setIsAdmin(false);
          setLoading(false);
          setError("Access denied. Management role required.");
          navigate('/');
          return;
        }

        setIsAdmin(true);
        loadBusinesses();
      } catch (err) {
        console.error('Error checking admin access:', err);
        setError("Error verifying admin access: " + err.message);
        setLoading(false);
      }
    };

    checkAdminAccess();
  }, [currentUser, navigate, routeEventId]);

  const normalizePrices = (raw) => {
    try {
      if (raw && typeof raw === 'object') {
        return Object.entries(raw).map(([key, value]) => ({
          id: key,
          priceDescription: value.priceDescription || '',
          priceContents: value.priceContents || '',
          type: value.type || 'ticket',
          count: value.count || 500,
          absorbFee: value.absorbFee !== undefined ? value.absorbFee : true,
          order: value.order || 0,
          price: value.price || 0,
          priceType: value.priceType || 'CREDIT',
          priceCurrency: value.priceCurrency || 'CAD',
          priceTax: value.priceTax || 0,
          soldout: value.soldout !== undefined ? value.soldout : false,
          fees: {
            original: value.fees?.original || 0,
            display: value.fees?.display || 0,
            discount: value.fees?.discount || 0,
            tax: value.fees?.tax || 0
          },
          imageURL: value.imageURL || '',
          min: value.min || 1,
          max: value.max || 10,
          priceDescriptions: value.priceDescriptions || [],
          priceIncludeTax: value.priceIncludeTax || false,
          saleStart: value.saleStart || firebase.firestore.Timestamp.fromDate(new Date()),
          saleEnd: value.saleEnd || firebase.firestore.Timestamp.fromDate(new Date()),
          priceId: value.priceId || ''
        }));
      }
    } catch (e) {
      // fallthrough
    }
    return [];
  };

  const loadBusinesses = async () => {
    try {
      setError("");
      setLoading(true);
      
      let businessesList = [];
      
      if (routeEventId) {
        // Load specific business by ID
        const doc = await fires.collection('events').doc(routeEventId).get();
        if (!doc.exists) {
          setError('Business not found');
          setBusinesses([]);
          setLoading(false);
          return;
        }
        
        const data = doc.data() || {};
        // Check if it's a business type
        if (data.type !== 'business') {
          setError('This event is not a business type');
          setBusinesses([]);
          setLoading(false);
          return;
        }
        
        const prices = normalizePrices(data.prices);
        businessesList = [{
          id: doc.id,
          title: data.title || data.eventDetails?.title || 'Untitled',
          prices: prices
        }];
      } else {
        // Load all events/businesses with type='business'
        let eventsSnapshot;
        try {
          eventsSnapshot = await fires
            .collection('events')
            .where('type', '==', 'business')
            .get();
        } catch (indexError) {
          // If index doesn't exist, get all and filter
          const allEvents = await fires.collection('events').get();
          const filtered = allEvents.docs.filter(doc => doc.data().type === 'business');
          eventsSnapshot = { docs: filtered };
        }
        
        eventsSnapshot.docs.forEach((doc) => {
          const data = doc.data() || {};
          const prices = normalizePrices(data.prices);
          
          businessesList.push({
            id: doc.id,
            title: data.title || data.eventDetails?.title || 'Untitled',
            prices: prices
          });
        });
      }
      
      setBusinesses(businessesList);
      setLoading(false);
    } catch (err) {
      console.error('Error loading businesses:', err);
      setError('Failed to load businesses: ' + err.message);
      setLoading(false);
    }
  };

  const handlePriceDescriptionChange = (bizIdx, priceId, value) => {
    setBusinesses(prev => prev.map((biz, idx) => {
      if (idx !== bizIdx) return biz;
      const updatedPrices = (biz.prices || []).map(p => 
        p.id === priceId ? { ...p, priceDescription: value } : p
      );
      return { ...biz, prices: updatedPrices };
    }));
  };

  const handlePriceTypeChange = (bizIdx, priceId, value) => {
    setBusinesses(prev => prev.map((biz, idx) => {
      if (idx !== bizIdx) return biz;
      const updatedPrices = (biz.prices || []).map(p => 
        p.id === priceId ? { ...p, type: value } : p
      );
      return { ...biz, prices: updatedPrices };
    }));
  };

  const handlePriceFieldChange = (bizIdx, priceId, field, value) => {
    setBusinesses(prev => prev.map((biz, idx) => {
      if (idx !== bizIdx) return biz;
      const updatedPrices = (biz.prices || []).map(p => {
        if (p.id === priceId) {
          if (field === 'count' || field === 'order' || field === 'price' || field === 'priceTax') {
            return { ...p, [field]: parseFloat(value) || 0 };
          } else if (field === 'absorbFee' || field === 'soldout' || field === 'priceIncludeTax') {
            return { ...p, [field]: value === true || value === 'true' };
          } else {
            return { ...p, [field]: value };
          }
        }
        return p;
      });
      return { ...biz, prices: updatedPrices };
    }));
  };

  const handlePriceContentsChange = (bizIdx, priceId, value) => {
    setBusinesses(prev => prev.map((biz, idx) => {
      if (idx !== bizIdx) return biz;
      const updatedPrices = (biz.prices || []).map(p => 
        p.id === priceId ? { ...p, priceContents: value } : p
      );
      return { ...biz, prices: updatedPrices };
    }));
  };

  const handlePriceFeeChange = (bizIdx, priceId, feeType, value) => {
    setBusinesses(prev => prev.map((biz, idx) => {
      if (idx !== bizIdx) return biz;
      const updatedPrices = (biz.prices || []).map(p => {
        if (p.id === priceId) {
          return {
            ...p,
            fees: {
              ...p.fees,
              [feeType]: parseFloat(value) || 0
            }
          };
        }
        return p;
      });
      return { ...biz, prices: updatedPrices };
    }));
  };

  const handlePriceImageChange = (bizIdx, priceId, imageURL) => {
    setBusinesses(prev => prev.map((biz, idx) => {
      if (idx !== bizIdx) return biz;
      const updatedPrices = (biz.prices || []).map(p => 
        p.id === priceId ? { ...p, imageURL: imageURL } : p
      );
      return { ...biz, prices: updatedPrices };
    }));
  };

  const handleUploadPriceImage = async (bizIdx, priceId, file) => {
    if (!file) return;
    
    setUploadingImageId(`price-${bizIdx}-${priceId}`);
    try {
      const response = await fetch('https://v1serviceapiuploadfile-650318705789.us-central1.run.app', {
        method: 'POST',
        headers: {
          'Access-Control-Allow-Origin': '*',
          'Content-Type': file.type
        },
        body: file
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      if (data.downloadURL) {
        handlePriceImageChange(bizIdx, priceId, data.downloadURL);
      } else {
        throw new Error('No downloadURL in response');
      }
    } catch (error) {
      console.error('Upload failed:', error);
      alert('Upload failed: ' + error.message);
    } finally {
      setUploadingImageId("");
    }
  };

  const handleAddPrice = (bizIdx) => {
    const newPriceId = uuid();
    setBusinesses(prev => prev.map((biz, idx) => {
      if (idx !== bizIdx) return biz;
      const newPrice = {
        id: newPriceId,
        priceDescription: "",
        priceContents: "",
        type: 'ticket',
        count: 500,
        absorbFee: true,
        order: 0,
        price: 0,
        priceType: 'CREDIT',
        priceCurrency: 'CAD',
        priceTax: 0,
        soldout: false,
        fees: {
          original: 0,
          display: 0,
          discount: 0,
          tax: 0
        },
        imageURL: '',
        min: 1,
        max: 10,
        priceDescriptions: [],
        priceIncludeTax: false,
        saleStart: firebase.firestore.Timestamp.fromDate(new Date()),
        saleEnd: firebase.firestore.Timestamp.fromDate(new Date()),
        priceId: ""
      };
      return { ...biz, prices: [...(biz.prices || []), newPrice] };
    }));
  };

  const handleRemovePrice = (bizIdx, priceId) => {
    if (!window.confirm('Are you sure you want to remove this price?')) {
      return;
    }
    setBusinesses(prev => prev.map((biz, idx) => {
      if (idx !== bizIdx) return biz;
      return { ...biz, prices: (biz.prices || []).filter(p => p.id !== priceId) };
    }));
  };

  const handleSave = async (biz) => {
    try {
      setSavingId(biz.id);
      
      // Get the current document to preserve existing data structure
      const doc = await fires.collection('events').doc(biz.id).get();
      const currentData = doc.data() || {};
      const currentPrices = currentData.prices || {};
      
      // Convert prices array back to object format
      const updatedPrices = { ...currentPrices };
      
      // Update existing prices and add new ones
      (biz.prices || []).forEach(price => {
        updatedPrices[price.id] = {
          priceDescription: price.priceDescription || '',
          priceContents: price.priceContents || '',
          type: price.type || 'ticket',
          count: price.count || 500,
          absorbFee: price.absorbFee !== undefined ? price.absorbFee : true,
          order: price.order || 0,
          price: price.price || 0,
          priceType: price.priceType || 'CREDIT',
          priceCurrency: price.priceCurrency || 'CAD',
          priceTax: price.priceTax || 0,
          soldout: price.soldout !== undefined ? price.soldout : false,
          fees: {
            original: parseFloat(price.fees?.original) || 0,
            display: parseFloat(price.fees?.display) || 0,
            discount: parseFloat(price.fees?.discount) || 0,
            tax: parseFloat(price.fees?.tax) || 0
          },
          imageURL: price.imageURL || '',
          min: price.min || 1,
          max: price.max || 10,
          priceDescriptions: price.priceDescriptions || [],
          priceIncludeTax: price.priceIncludeTax || false,
          saleStart: price.saleStart || firebase.firestore.Timestamp.fromDate(new Date()),
          saleEnd: price.saleEnd || firebase.firestore.Timestamp.fromDate(new Date()),
          priceId: price.priceId || ''
        };
      });
      
      // Remove prices that were deleted
      const existingPriceIds = new Set((biz.prices || []).map(p => p.id));
      Object.keys(updatedPrices).forEach(priceId => {
        if (!existingPriceIds.has(priceId)) {
          delete updatedPrices[priceId];
        }
      });
      
      // Update the document
      await fires.collection('events').doc(biz.id).update({
        prices: updatedPrices
      });
      
      setSavingId("");
      alert('Prices saved successfully!');
    } catch (err) {
      console.error('Error saving prices:', err);
      alert('Failed to save prices: ' + err.message);
      setSavingId("");
    }
  };

  const filteredBusinesses = businesses.filter(biz => 
    biz.title.toLowerCase().includes(query.toLowerCase()) ||
    biz.id.toLowerCase().includes(query.toLowerCase())
  );

  if (loading) {
    return (
      <Container className="mt-4">
        <div className="text-center">
          <Spinner animation="border" role="status">
            <span className="visually-hidden">Loading...</span>
          </Spinner>
        </div>
      </Container>
    );
  }

  if (!isAdmin) {
    return (
      <Container className="mt-4">
        <Alert variant="danger">{error || "Access denied"}</Alert>
      </Container>
    );
  }

  return (
    <Container fluid className="mt-4">
      <Row className="mb-4">
        <Col>
          <h2>Business Price Management</h2>
          <p className="text-muted">Manage prices for all businesses/events</p>
        </Col>
      </Row>

      {error && (
        <Row className="mb-3">
          <Col>
            <Alert variant="danger" onClose={() => setError("")} dismissible>
              {error}
            </Alert>
          </Col>
        </Row>
      )}

      <Row className="mb-3">
        <Col md={6}>
          <InputGroup>
            <Form.Control
              type="text"
              placeholder="Search by business name or ID..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
          </InputGroup>
        </Col>
      </Row>

      {filteredBusinesses.length === 0 ? (
        <Row>
          <Col>
            <Card>
              <Card.Body className="text-center">
                <p className="text-muted">No businesses found</p>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      ) : (
        filteredBusinesses.map((biz, bizIdx) => (
          <Row key={biz.id} className="mb-4">
            <Col>
              <Card className="shadow-sm">
                <Card.Header className="d-flex justify-content-between align-items-center">
                  <div>
                    <h5 className="mb-0">{biz.title}</h5>
                    <small className="text-muted">ID: {biz.id}</small>
                  </div>
                  <div>
                    <Button
                      variant="success"
                      size="sm"
                      onClick={() => handleAddPrice(bizIdx)}
                      className="me-2"
                    >
                      Add Price
                    </Button>
                    <Button
                      variant="primary"
                      size="sm"
                      onClick={() => handleSave(biz)}
                      disabled={savingId === biz.id}
                    >
                      {savingId === biz.id ? (
                        <>
                          <Spinner size="sm" animation="border" className="me-2" />
                          Saving...
                        </>
                      ) : (
                        'Save Changes'
                      )}
                    </Button>
                  </div>
                </Card.Header>
                <Card.Body>
                  {(biz.prices || []).length === 0 ? (
                    <div className="text-muted text-center py-3">
                      No prices configured. Click "Add Price" to create one.
                    </div>
                  ) : (
                    <div style={{ overflowX: 'auto' }}>
                      <Table bordered responsive>
                        <thead>
                          <tr>
                            <th style={{ minWidth: '150px' }}>Price ID</th>
                            <th style={{ minWidth: '200px' }}>Name</th>
                            <th style={{ minWidth: '120px' }}>Type</th>
                            <th style={{ minWidth: '150px' }}>Description</th>
                            <th style={{ minWidth: '100px' }}>Price</th>
                            <th style={{ minWidth: '100px' }}>Count</th>
                            <th style={{ minWidth: '100px' }}>Order</th>
                            <th style={{ minWidth: '100px' }}>Price Type</th>
                            <th style={{ minWidth: '100px' }}>Currency</th>
                            <th style={{ minWidth: '100px' }}>Price Tax</th>
                            <th style={{ minWidth: '100px' }}>Absorb Fee</th>
                            <th style={{ minWidth: '100px' }}>Sold Out</th>
                            <th style={{ minWidth: '120px' }}>Original</th>
                            <th style={{ minWidth: '120px' }}>Display</th>
                            <th style={{ minWidth: '120px' }}>Discount</th>
                            <th style={{ minWidth: '150px' }}>Tax</th>
                            <th style={{ minWidth: '150px' }}>Image</th>
                            <th style={{ minWidth: '100px' }}>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(biz.prices || []).map((price) => (
                            <tr key={price.id}>
                              <td>
                                <Form.Control
                                  size="sm"
                                  type="text"
                                  value={price.id}
                                  readOnly
                                  style={{ fontFamily: 'monospace', fontSize: '0.85em' }}
                                />
                              </td>
                              <td>
                                <Form.Control
                                  size="sm"
                                  type="text"
                                  value={price.priceDescription || ''}
                                  onChange={(e) => handlePriceDescriptionChange(bizIdx, price.id, e.target.value)}
                                  placeholder="Price name"
                                />
                              </td>
                              <td>
                                <Form.Select
                                  size="sm"
                                  value={price.type || 'ticket'}
                                  onChange={(e) => handlePriceTypeChange(bizIdx, price.id, e.target.value)}
                                >
                                  <option value="ticket">Ticket</option>
                                  <option value="partyroom">Party Room</option>
                                  <option value="booth">Booth</option>
                                </Form.Select>
                              </td>
                              <td>
                                <Form.Control
                                  size="sm"
                                  type="text"
                                  as="textarea"
                                  rows={2}
                                  value={price.priceContents || ''}
                                  onChange={(e) => handlePriceContentsChange(bizIdx, price.id, e.target.value)}
                                  placeholder="Price description"
                                />
                              </td>
                              <td>
                                <Form.Control
                                  size="sm"
                                  type="number"
                                  step="0.01"
                                  min="0"
                                  value={price.price || 0}
                                  onChange={(e) => handlePriceFieldChange(bizIdx, price.id, 'price', e.target.value)}
                                  placeholder="0.00"
                                />
                              </td>
                              <td>
                                <Form.Control
                                  size="sm"
                                  type="number"
                                  min="0"
                                  value={price.count || 500}
                                  onChange={(e) => handlePriceFieldChange(bizIdx, price.id, 'count', e.target.value)}
                                  placeholder="500"
                                />
                              </td>
                              <td>
                                <Form.Control
                                  size="sm"
                                  type="number"
                                  min="0"
                                  value={price.order || 0}
                                  onChange={(e) => handlePriceFieldChange(bizIdx, price.id, 'order', e.target.value)}
                                  placeholder="0"
                                />
                              </td>
                              <td>
                                <Form.Select
                                  size="sm"
                                  value={price.priceType || 'CREDIT'}
                                  onChange={(e) => handlePriceFieldChange(bizIdx, price.id, 'priceType', e.target.value)}
                                >
                                  <option value="CREDIT">CREDIT</option>
                                  <option value="DEBIT">DEBIT</option>
                                  <option value="CASH">CASH</option>
                                </Form.Select>
                              </td>
                              <td>
                                <Form.Select
                                  size="sm"
                                  value={price.priceCurrency || 'CAD'}
                                  onChange={(e) => handlePriceFieldChange(bizIdx, price.id, 'priceCurrency', e.target.value)}
                                >
                                  <option value="CAD">CAD</option>
                                  <option value="USD">USD</option>
                                  <option value="EUR">EUR</option>
                                </Form.Select>
                              </td>
                              <td>
                                <Form.Control
                                  size="sm"
                                  type="number"
                                  step="0.01"
                                  min="0"
                                  value={price.priceTax || 0}
                                  onChange={(e) => handlePriceFieldChange(bizIdx, price.id, 'priceTax', e.target.value)}
                                  placeholder="0.00"
                                />
                              </td>
                              <td>
                                <Form.Check
                                  type="checkbox"
                                  checked={price.absorbFee !== undefined ? price.absorbFee : true}
                                  onChange={(e) => handlePriceFieldChange(bizIdx, price.id, 'absorbFee', e.target.checked)}
                                />
                              </td>
                              <td>
                                <Form.Check
                                  type="checkbox"
                                  checked={price.soldout !== undefined ? price.soldout : false}
                                  onChange={(e) => handlePriceFieldChange(bizIdx, price.id, 'soldout', e.target.checked)}
                                />
                              </td>
                              <td>
                                <Form.Control
                                  size="sm"
                                  type="number"
                                  step="0.01"
                                  min="0"
                                  value={price.fees?.original || 0}
                                  onChange={(e) => handlePriceFeeChange(bizIdx, price.id, 'original', e.target.value)}
                                  placeholder="0.00"
                                />
                              </td>
                              <td>
                                <Form.Control
                                  size="sm"
                                  type="number"
                                  step="0.01"
                                  min="0"
                                  value={price.fees?.display || 0}
                                  onChange={(e) => handlePriceFeeChange(bizIdx, price.id, 'display', e.target.value)}
                                  placeholder="0.00"
                                />
                              </td>
                              <td>
                                <Form.Control
                                  size="sm"
                                  type="number"
                                  step="0.01"
                                  min="0"
                                  value={price.fees?.discount || 0}
                                  onChange={(e) => handlePriceFeeChange(bizIdx, price.id, 'discount', e.target.value)}
                                  placeholder="0.00"
                                />
                              </td>
                              <td>
                                <Form.Control
                                  size="sm"
                                  type="number"
                                  step="0.01"
                                  min="0"
                                  value={price.fees?.tax || 0}
                                  onChange={(e) => handlePriceFeeChange(bizIdx, price.id, 'tax', e.target.value)}
                                  placeholder="0.00"
                                  style={{ minWidth: '140px' }}
                                />
                              </td>
                              <td>
                                {price.imageURL && (
                                  <img
                                    src={price.imageURL}
                                    alt="Price"
                                    style={{ width: '50px', height: '50px', objectFit: 'cover', marginRight: '10px' }}
                                  />
                                )}
                                <div className="mt-2">
                                  <Form.Control
                                    size="sm"
                                    type="text"
                                    value={price.imageURL || ''}
                                    onChange={(e) => handlePriceImageChange(bizIdx, price.id, e.target.value)}
                                    placeholder="Image URL"
                                  />
                                  <div className="mt-1">
                                    <input
                                      type="file"
                                      accept="image/*"
                                      onChange={(e) => {
                                        const file = e.target.files[0];
                                        if (file) {
                                          handleUploadPriceImage(bizIdx, price.id, file);
                                        }
                                      }}
                                      style={{ fontSize: '0.85em' }}
                                      disabled={uploadingImageId === `price-${bizIdx}-${price.id}`}
                                    />
                                    {uploadingImageId === `price-${bizIdx}-${price.id}` && (
                                      <Spinner size="sm" animation="border" className="ms-2" />
                                    )}
                                  </div>
                                </div>
                              </td>
                              <td>
                                <Button
                                  variant="outline-danger"
                                  size="sm"
                                  onClick={() => handleRemovePrice(bizIdx, price.id)}
                                >
                                  Remove
                                </Button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </div>
                  )}
                </Card.Body>
              </Card>
            </Col>
          </Row>
        ))
      )}
    </Container>
  );
}

